import React from 'react'

import { Button, Image, Popover } from '../../../module'
import styles from './_select-display.module.scss'
import ScrollingContainer from '../../ScrollingContainer/ScrollingContainer'

export type SelectDisplayOption = {
  /** Identifier for an option */
  name: string
  /** Label display for an option */
  label: string
  /** Object of tippy to show children on option value */
  children?: {
    /** Array of options for tippy */
    options: SelectDisplayOption[]
  }
  /** Optional secondary content to display */
  secondaryContent?: React.ReactNode
  /** Optional image to appear to the left of the option */
  image?: string
}

export type SelectDisplayProps<OptionItem extends SelectDisplayOption> = {
  /** Array of options */
  options: OptionItem[]
  /** Function to be called when an option is clicked */
  callout: (option: OptionItem) => void
  /** Determines if there is an active option to be highlighted */
  active?: keyof OptionItem
  /** Adds a border around the component */
  hasBorder?: boolean
  /** Adds a tippy style around the component */
  hasTippy?: boolean
  /** Optional maxHeight for the display. Enables scrolling of the overflow. */
  maxHeight?: number | string
  /** Optional prop to add a test id to the SelectDisplay for QA testing */
  qaTestId?: string
}

const SelectDisplay = <OptionItem extends SelectDisplayOption>({
  options,
  callout,
  active,
  hasBorder,
  hasTippy,
  maxHeight,
  qaTestId = 'select-display',
}: SelectDisplayProps<OptionItem>): React.JSX.Element => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const containerHeight = containerRef.current?.clientHeight

  const getOptions = (filteredOptions: OptionItem[]) => {
    const display = filteredOptions.map((o) => {
      return (
        <SelectDisplayRow
          key={o.name}
          callout={callout}
          active={active}
          option={o}
          hasBorder={hasBorder}
          getOptions={getOptions}
        />
      )
    })
    return (
      <div
        className={`${styles.display} ${hasBorder ? styles.border : ''} ${
          hasTippy ? styles.displayTippy : ''
        }`}
        style={{ maxHeight }}
        ref={containerRef}
        qa-test-id={qaTestId}
      >
        {maxHeight ? (
          <ScrollingContainer
            height={
              containerHeight &&
              typeof maxHeight === 'number' &&
              containerHeight < maxHeight - (hasBorder ? 2 : 0)
                ? 'fit-content'
                : containerHeight
            }
          >
            <div className={styles.scrollMargin}>{display}</div>
          </ScrollingContainer>
        ) : (
          display
        )}
      </div>
    )
  }
  return <>{getOptions(options)}</>
}

export default SelectDisplay

export const SelectDisplayRow = <OptionItem extends SelectDisplayOption>({
  callout,
  active,
  option,
  hasBorder,
  getOptions,
}: {
  callout: (option: OptionItem) => void
  active?: keyof OptionItem
  option: OptionItem
  getOptions?: (filteredOptions: OptionItem[]) => React.JSX.Element
  hasBorder?: boolean
}) => {
  const showPopover = option.children && option.children?.options?.length > 0

  const row = (visible?: boolean, setVisible?: (visible: boolean) => void) => {
    return (
      <Button
        as='unstyled'
        onClick={(e) => {
          e.stopPropagation()
          callout(option)
          if (showPopover) {
            setVisible?.(!visible)
          }
        }}
        id={option.name}
        className={`${styles.row} ${hasBorder ? styles.withBorder : ''}`}
      >
        <div className={styles.innerRowContainer}>
          <div
            className={`${styles.leftSide} ${option.image ? styles.hasImage : ''}`}
          >
            {option.image ? (
              <Image
                url={option.image}
                style={{
                  width: 16,
                  height: 16,
                }}
              />
            ) : null}
            <span className={`${active === option.name ? styles.active : ''}`}>
              {option.label}
            </span>
          </div>
        </div>
        {option.secondaryContent ? (
          <span className={styles.secondaryContent}>
            {option.secondaryContent}
          </span>
        ) : null}
      </Button>
    )
  }

  return (
    <>
      {showPopover && getOptions ? (
        <Popover
          position='bottom-start'
          noPadding
          popoverContent={getOptions(option?.children?.options as OptionItem[])}
        >
          {({ visible, setVisible }) => row(visible, setVisible)}
        </Popover>
      ) : (
        row()
      )}
    </>
  )
}
